import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Brace-safe View Payment Details modal. Uses simple conditionals and balanced JSX.
 */
const ViewPaymentDetailsModal = ({ isOpen, onClose, payment, translateStatus }) => {
  const { t } = useTranslation('navigation');

  if (!isOpen || !payment) {
    return null;
  }

  const customerName =
    payment.customer_first_name && payment.customer_last_name
      ? `${payment.customer_first_name} ${payment.customer_last_name}`
      : payment.customer_email || 'Unknown Customer';

  const dateDisplay = payment.payment_date
    ? new Date(payment.payment_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : payment.created_at
    ? new Date(payment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A';

  const approvedAtDisplay = payment.approved_at
    ? new Date(payment.approved_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

  const statusLabel = translateStatus ? translateStatus(payment.status) : payment.status;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payment-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Payment Request Details</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="payment-details-container">
            <div className="detail-section">
              <div className="detail-item">
                <span className="detail-label">Customer:</span>
                <span className="detail-value">{customerName}</span>
              </div>
              {payment.customer_email ? (
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value email-value">{payment.customer_email}</span>
                </div>
              ) : null}
              {payment.customer_phone ? (
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{payment.customer_phone}</span>
                </div>
              ) : null}
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`status-badge ${payment.status}`}>{statusLabel}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Payment Request #:</span>
                <span className="detail-value">{payment.payment_request_number || `MP-${payment.id}`}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value amount-value">
                  {payment.amount} {payment.currency || 'DA'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Description:</span>
                <span className="detail-value">{payment.description || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date:</span>
                <span className="detail-value">{dateDisplay}</span>
              </div>
              {payment.agent_name ? (
                <div className="detail-item">
                  <span className="detail-label">Created by:</span>
                  <span className="detail-value">{payment.agent_name}</span>
                </div>
              ) : null}
              {payment.status === 'approved' && payment.moderator_name ? (
                <div className="detail-item">
                  <span className="detail-label">Approved by:</span>
                  <span className="detail-value">{payment.moderator_name}</span>
                </div>
              ) : null}
              {payment.status === 'approved' && payment.approved_at ? (
                <div className="detail-item">
                  <span className="detail-label">Approved on:</span>
                  <span className="detail-value">{approvedAtDisplay}</span>
                </div>
              ) : null}
              {payment.status === 'rejected' && payment.rejection_reason ? (
                <div className="detail-item">
                  <span className="detail-label">Rejection Reason:</span>
                  <span className="detail-value rejection-reason">{payment.rejection_reason}</span>
                </div>
              ) : null}
              {payment.notes ? (
                <div className="detail-item">
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value">{payment.notes}</span>
                </div>
              ) : null}
            </div>
            {payment.receipt_image_url ? (
              <div className="receipt-section">
                <h4>Receipt Image</h4>
                <div className="receipt-image-container">
                  <img
                    src={payment.receipt_image_url}
                    alt="Receipt"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => window.open(payment.receipt_image_url, '_blank')}
                  style={{ marginTop: '12px' }}
                >
                  <span className="material-icons">open_in_new</span>
                  View Full Size
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentDetailsModal;
